import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'

import PageContext from '@contexts/pageContext'
import { useModals, useSetState } from '@campaignhub/react-hooks'
import { useParams } from 'react-router-dom'

import { Box, Columns, ModalContext } from '@campaignhub/suit-theme'

import serviceJobNoteTypes from '@functions/serviceJobNoteTypes'

import useCurrentUser from '@hooks/useCurrentUser'
import useReduxAction from '@hooks/useReduxAction'
import useServiceJob from '@hooks/useServiceJob'
import useUploadResource from '@hooks/useUploadResource'
import useUploadAsset from '@hooks/useUploadAsset'
import useResourceTypes from '@hooks/useResourceTypes'

import ChangeJobAssignmentModal from '@modals/ChangeJobAssignmentModal'
import EditResourceDetailModal from '@modals/EditResourceDetailModal'
import UploadModal from '@modals/UploadModal'

import ServiceJobNote from '@components/ServiceJobNote'
import ActivityLogs from './components/ActivityLogs'
import AppointmentDetails from './components/AppointmentDetails'
import AssetsAndResources from './components/AssetsAndResources'
import AssignedUsers from './components/AssignedUsers'
import AvailableResources from './components/AvailableResources'
import ClientProductionNotes from './components/ClientProductionNotes'
import DuskPositional from './components/DuskPositional'
import JobDetails from './components/JobDetails'
import JobHeader from './components/JobHeader'
import JobSummary from './components/JobSummary'
import ListingDetails from './components/ListingDetails'
import ProductDetails from './components/ProductDetails'
import ProductionNotes from './components/ProductionNotes'
import ProviderChecklist from './components/ProviderChecklist'
import StudioFeedback from './components/StudioFeedback'

const assignServiceJobStatus = (serviceJobParam, createFn, updateWorkflow) => {
  createFn(serviceJobParam).then(({ errors, success }) => {
    if (!success && errors){
      toast.warning(errors[0])
      return
    }
    updateWorkflow(serviceJobParam.serviceJobStatusId)
  })
}

const assignServiceJobUser = (serviceJobUserParams, createFn, setState) => {
  createFn(serviceJobUserParams).then(({ success, errors }) => {
    if (!success){
      toast.warning(errors[0])
      return
    }

    setState({ showChangeJobAssignmentModal: false })
  })
}

const updateServiceJobUser = (serviceJobUser, updateFn, setState) => {
  updateFn(serviceJobUser).then(({ success, errors }) => {
    if (!success && errors){
      toast.warning(errors[0])
      return
    }

    setState({ showChangeJobAssignmentModal: false })
  })
}

const updateResource = (resource, updateFn, setState) => {
  updateFn(resource).then(({ success, errors }) => {
    if (!success && errors){
      toast.warning(errors[0])
      return
    }

    setState({ showEditResourceDetailModal: false })
  })
}

const callbacks = (component, props, setState, progressRef, uploadsRef, callback) => {
  const progress = progressRef
  const uploads = uploadsRef
  const componentCallbacks = {
    ChangeJobAssignmentModal: {
      closeModal: () => setState({ showChangeJobAssignmentModal: false }),
      assignServiceJobUser: (serviceJobUserParams, createFn) => assignServiceJobUser(serviceJobUserParams, createFn, setState),
      updateServiceJobUser: (serviceJobUser, updateFn) => updateServiceJobUser(serviceJobUser, updateFn, setState),
    },
    EditResourceDetailModal: {
      closeModal: () => setState({ showEditResourceDetailModal: false }),
      updateResource: (resource, updateFn) => updateResource(resource, updateFn, setState),
    },
    ResourceUploadModal: {
      checkProgress: (progressStatus) => {
        progress.current = progressStatus
      },
      checkUploadStatus: (uploadStatus) => {
        uploads.current = uploadStatus
      },
      closeModal: () => {
        if ((progress.current && progress.current !== 0 && uploads.current.length === 0) || (progress.current === 0 && uploads.current.length === 0)){
          if (window.confirm('This action will close the modal and interrupt the ongoing upload.\nDo you still wish to close?')){
            progress.current = undefined
            uploads.current = []
            setState({ showResourceUploadModal: false })
          }
        } else {
          progress.current = undefined
          uploads.current = []
          setState({ showResourceUploadModal: false })
        }
      },
      assignServiceJobStatus: (serviceJobParams, createFn) => assignServiceJobStatus(serviceJobParams, createFn, callback),
    },
    AssetUploadModal: {
      checkProgress: (progressStatus) => {
        progress.current = progressStatus
      },
      checkUploadStatus: (uploadStatus) => {
        uploads.current = uploadStatus
      },
      closeModal: () => {
        if ((progress.current && progress.current !== 0 && uploads.current.length === 0) || (progress.current === 0 && uploads.current.length === 0)){
          if (window.confirm('This action will close the modal and interrupt the ongoing upload.\nDo you still wish to close?')){
            progress.current = undefined
            uploads.current = []
            setState({ showAssetUploadModal: false })
          }
        } else {
          progress.current = undefined
          uploads.current = []
          setState({ showAssetUploadModal: false })
        }
      },
      assignServiceJobStatus: (serviceJobParams, createFn) => assignServiceJobStatus(serviceJobParams, createFn, callback),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showChangeJobAssignmentModal: false,
  showEditResourceDetailModal: false,
  showResourceUploadModal: false,
  showAssetUploadModal: false,
  allowedResourceFileTypes: 'image/*',
  loaded: false,
  duskContent: '',
  duskTotalImage: 0,
}

const ServiceJob = (props) => {
  const { userType } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showChangeJobAssignmentModal,
    showEditResourceDetailModal,
    showResourceUploadModal,
    workflowUpdated,
    showAssetUploadModal,
    allowedResourceFileTypes,
    duskContent,
    duskTotalImage,
  } = state

  const modalContext = useModals()
  const { callbacks: { setModalData } } = modalContext

  const { id: serviceJobId } = useParams()
  const { currentUser } = useCurrentUser()

  const includes = ['campaign', 'client', 'details', 'histories', 'postProductionChecklistItems', 'service', 'users', 'notes']
  useReduxAction('serviceJobs', 'loadServiceJobV1', {
    includes: includes.join(','),
  }, [serviceJobId], {
    dispatchAction: (action, requestOptions) => action(serviceJobId, requestOptions),
    shouldPerformFn: (entityReducer) => {
      const { loadedIds } = entityReducer
      return serviceJobId && !loadedIds.includes(serviceJobId)
    },
  })

  const updateDuskDetail = (content, totalImage) => {
    setState({ duskContent: content, duskTotalImage: totalImage })
  }

  const {
    callbacks: {
      assignServiceJobStatus: createFnServiceJobStatus,
    },
    serviceJob,
    filteredServiceResourceTypes,
    showDuskPositional,
    loading,
  } = useServiceJob({ id: serviceJobId })
  const modalRequestOptions = { ServiceJobId: serviceJob.id }

  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceGroups, serviceJobDetails, serviceJobs } = entities

  const updateWorkflow = (statusId) => {
    serviceJob.serviceJobStatusId = statusId
    setState({ workflowUpdated: !workflowUpdated })
  }

  const useUploadResourcePayload = useUploadResource()
  const {
    callbacks: {
      deleteResource: deleteFnResource,
      loadResource: loadFnResource,
    },
  } = useUploadResourcePayload

  const useUploadAssetPayload = useUploadAsset()
  const {
    callbacks: {
      deleteAsset: deleteFnAsset,
      loadAsset: loadFnAsset,
    },
  } = useUploadAssetPayload

  const pageContext = {
    callbacks: {
      showChangeJobAssignmentModal: (payload) => {
        setModalData('ChangeJobAssignmentModal', payload)
        setState({ showChangeJobAssignmentModal: true })
      },
      showEditResourceDetailModal: (payload) => {
        setModalData('EditResourceDetailModal', payload)
        setState({ showEditResourceDetailModal: true })
      },
      showResourceUploadModal: (payload) => {
        setModalData('showResourceUploadModal', payload)
        setState({ showResourceUploadModal: true })
      },
      showAssetUploadModal: (payload) => {
        setModalData('showAssetUploadModal', payload)
        setState({ showAssetUploadModal: true })
      },
      assignServiceJobStatus: (serviceJobUserParams, createFn) => {
        assignServiceJobStatus(serviceJobUserParams, createFn, updateWorkflow)
      },
      toggleUpdateDuskDetail: (content, totalImage) => updateDuskDetail(content, totalImage),
    },
    currentUser,
    duskContent,
    duskTotalImage,
    serviceJob,
    serviceJobs,
    showDuskPositional,
    workflowUpdated,
  }

  useEffect(() => {
    setState({ allowedResourceFileTypes: serviceGroups[serviceJob.serviceGroupId]?.allowedResourceFileTypes })
  }, [serviceJob.serviceGroupId, serviceGroups])

  useResourceTypes()

  const details = Object.values(serviceJobDetails).filter(x => serviceJob?.details?.includes(x.id))
  const providerChecklist = details.filter(x => x.entityFieldType.name === 'ProviderChecklist')
  const productionNotes = details.filter(x => x.entityFieldType.name === 'ClientStudioNote')

  const progressRef = useRef()
  const uploadsRef = useRef()

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <JobHeader campaign={serviceJob.campaign} details={details} />
        <Box paddingX="large" paddingTop={[112, 105]}>
          <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
            <Columns.Main>
              <AssetsAndResources
                hideProof={['Copywriting'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)}
                resourceTypes={filteredServiceResourceTypes}
                serviceJob={serviceJob}
                showProof={['Photography', 'Floorplan'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)}
                showResourcesOnly={(userType === 'Onsite' && !['Copywriting'].includes(serviceGroups[serviceJob.serviceGroupId]?.name))
                || !['Copywriting', 'Photography', 'Floorplan'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)}
                workflowUpdated={workflowUpdated}
              />
              <JobSummary
                campaign={serviceJob.campaign}
                client={serviceJob.client}
                details={details}
                totalAssets={serviceJob.totalAssets}
                showJobLength={userType === 'Onsite'}
              />
              <ClientProductionNotes
                clientProductionNotes={serviceJob.client?.productionNote}
                hidden={userType !== 'Onsite'}
              />
              {!loading && (
                <ProviderChecklist
                  providerChecklist={providerChecklist}
                  hidden={userType !== 'Onsite'}
                />
              )}
              {(['Copywriting'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)
              || (!['Copywriting'].includes(serviceGroups[serviceJob.serviceGroupId]?.name) && userType === 'Studio'))
              && (
                <ProductionNotes productionNotes={productionNotes} />
              )}
              <AvailableResources hidden />
              <ServiceJobNote
                dashboardModule
                editable
                title="Provider Notes"
                serviceJobId={serviceJob.id}
                serviceJobNoteTypeId={serviceJobNoteTypes.provider.id}
              />
              {showDuskPositional && (
                <DuskPositional details={details} serviceJobId={serviceJob.id} />
              )}
              <StudioFeedback hidden />
              <ListingDetails hidden client={serviceJob.client} />
              <ProductDetails hidden service={serviceJob.service} details={details} />
              <ActivityLogs hidden />
            </Columns.Main>
            <Columns.Sidebar>
              <JobDetails hidden={userType === 'Onsite'} />
              <AssignedUsers hidden={userType === 'Onsite'} />
              <AppointmentDetails dateService={serviceJob.dateService} details={details} />
            </Columns.Sidebar>
          </Columns>
        </Box>
        <ChangeJobAssignmentModal
          callbacks={callbacks('ChangeJobAssignmentModal', props, setState)}
          showModal={showChangeJobAssignmentModal}
          serviceJob={serviceJob}
        />
        <EditResourceDetailModal
          callbacks={callbacks('EditResourceDetailModal', props, setState)}
          showModal={showEditResourceDetailModal}
        />
        <UploadModal
          callbacks={{
            ...callbacks('ResourceUploadModal', props, setState, progressRef, uploadsRef, updateWorkflow),
            deleteFn: deleteFnResource,
            loadFn: loadFnResource,
            assignFn: createFnServiceJobStatus,
          }}
          serviceJob={serviceJob}
          serviceJobOptions={modalRequestOptions}
          showModal={showResourceUploadModal}
          title="Upload"
          titleSecondLine="Resources"
          type="Resources"
          resourceTypes={filteredServiceResourceTypes}
          fileTypesAllowed={allowedResourceFileTypes}
        />
        <UploadModal
          callbacks={{
            ...callbacks('AssetUploadModal', props, setState, progressRef, uploadsRef, updateWorkflow),
            deleteFn: deleteFnAsset,
            loadFn: loadFnAsset,
            assignFn: createFnServiceJobStatus,
          }}
          serviceJob={serviceJob}
          serviceJobOptions={modalRequestOptions}
          showModal={showAssetUploadModal}
          title="Upload"
          titleSecondLine="Assets"
          type="Assets"
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

ServiceJob.propTypes = {
  userType: PropTypes.string,
}

export default ServiceJob
